import React, { useState } from 'react';
import { Calendar, Clock, Ship, MapPin } from 'lucide-react';

import ShipmentRouteDrawer from '@/components/shipment-route-drawer';
import { TimelineComponent } from '@/features/calendar/components/timeline-component';
import { CalendarComponent } from '@/features/calendar/components/calendar-component';
import { useTimelineShipments, TimelineShipment } from '@/features/calendar/hooks/useTimelineShipments';

// Main Container
const ShipmentTimelineView: React.FC = () => {
  const [viewMode, setViewMode] = useState<'calendar' | 'timeline'>('calendar');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { timelineShipments, isLoading, error, selectedShipment, setSelectedShipment, shipmentEvents } =
    useTimelineShipments();

  const handleShipmentClick = (shipment: TimelineShipment) => {
    setSelectedShipment(shipment);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedShipment(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading timeline...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-2">Error loading timeline</div>
          <div className="text-gray-600 text-sm">{error.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-gray-900">Shipment Timeline</h1>
            <span className="text-sm text-gray-500">{timelineShipments?.length} shipments</span>

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1
                                    ${
                                      viewMode === 'calendar'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }
                                `}
              >
                <Calendar className="w-4 h-4" />
                Calendar
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1
                                    ${
                                      viewMode === 'timeline'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }
                                `}
              >
                <Clock className="w-4 h-4" />
                Timeline
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 h-[calc(100vh-180px)]">
        {viewMode === 'timeline' ? (
          <TimelineComponent shipmentEvents={timelineShipments || []} onShipmentClick={handleShipmentClick} />
        ) : (
          <CalendarComponent shipmentEvents={timelineShipments || []} onShipmentClick={handleShipmentClick} />
        )}

        {/* Shipment Details Drawer */}
        {drawerOpen && selectedShipment && (
          <ShipmentRouteDrawer
            isOpen={drawerOpen}
            onClose={closeDrawer}
            shipmentData={selectedShipment}
            shipmentEvents={shipmentEvents}
          />
        )}

        {/* Legend */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
          <div className="flex flex-wrap gap-4">
            {viewMode === 'timeline' ? (
              <>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-white border border-gray-300 rounded-full"></div>
                  <span className="text-xs text-gray-600">Has tracking events</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-0.5 border-t-2 border-dashed border-red-500"></div>
                  <span className="text-xs text-gray-600">Current day</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-0.5 border-t-2 border-dashed border-blue-500"></div>
                  <span className="text-xs text-gray-600">Hover date</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <Ship className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-gray-600">Departure</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-gray-600">Arrival</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                  <span className="text-xs text-gray-600">Has tracking events (●)</span>
                </div>
              </>
            )}
            <div className="flex items-center space-x-2 text-xs text-gray-600">
              <span>Colors represent different carriers</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentTimelineView;
